/**
 * SatelliteMap — orthophoto de la parcelle servie par la Géoplateforme IGN
 * avec un overlay SVG de la parcelle (liseré + fill lime translucide).
 *
 * Tech :
 *  - Une seule requête WMS GetMap (pas de stitching de tuiles)
 *    → https://data.geopf.fr/wms-r/wms
 *    → couche ORTHOIMAGERY.ORTHOPHOTOS (dernières campagnes IGN, public domain)
 *    → CORS ouvert, aucune clé, aucun quota bloquant
 *  - On fixe la WIDTH/HEIGHT à 600×400 (en pratique on demande 2× en retina)
 *    et on construit la BBOX avec un ratio "métrique" de 1.5 en tenant compte
 *    de cos(lat). Le WMS étire linéairement BBOX→WIDTH×HEIGHT, donc avec un
 *    BBOX métrique-correct le rendu final est visuellement juste.
 *  - Overlay SVG : projection linéaire degrés→pixels (même mapping que WMS)
 *    pour que le polygone parcellaire s'aligne exactement avec l'imagerie.
 */

import { useMemo } from "react";
import { Pill } from "./ui";
import { Ic } from "./icons";
import type { CadastreParcel } from "../lib/types";
import { parcelLabel } from "../lib/parcelMerge";

// Keep in sync with ParcelMap's viewBox — consumers can hot-swap the two
// without the surrounding layout jumping.
const VIEW_W = 600;
const VIEW_H = 400;
const TARGET_METRIC_RATIO = VIEW_W / VIEW_H; // 1.5

// Padding in meters around the parcel bbox. Loose enough that neighbors and a
// bit of street context are visible, tight enough that the parcel stays the
// hero of the image.
const PADDING_M = 30;

// WMS base URL — served by the Géoplateforme IGN (the successor to the legacy
// wxs.ign.fr). Free tier, CORS `*`, no API key required.
const WMS_URL = "https://data.geopf.fr/wms-r/wms";

type GeoJSONPolygon = {
  type: "Polygon";
  coordinates: number[][][];
};
type GeoJSONMultiPolygon = {
  type: "MultiPolygon";
  coordinates: number[][][][];
};
type AnyGeom = GeoJSONPolygon | GeoJSONMultiPolygon | null | undefined;

export function SatelliteMap({
  parcel,
  className = "",
}: {
  parcel: CadastreParcel;
  className?: string;
}) {
  const subjectGeom = parcel.geometry as AnyGeom;

  const view = useMemo(() => {
    const bb = bboxOfGeom(subjectGeom);
    if (!bb) return null;
    const padded = expandBbox(bb, PADDING_M);
    const fitted = fitToMetricRatio(padded, TARGET_METRIC_RATIO);

    const project = (lng: number, lat: number) => [
      ((lng - fitted.minLng) / (fitted.maxLng - fitted.minLng)) * VIEW_W,
      ((fitted.maxLat - lat) / (fitted.maxLat - fitted.minLat)) * VIEW_H,
    ];

    // WMS 1.3.0 + EPSG:4326 expects BBOX in lat,lng order. Ask for 2× pixels
    // so HiDPI screens render crisp. 1200×800 JPEG ≈ 150–250 kB per parcel.
    const bbox = [
      fitted.minLat,
      fitted.minLng,
      fitted.maxLat,
      fitted.maxLng,
    ]
      .map((n) => n.toFixed(6))
      .join(",");
    const qs = new URLSearchParams({
      SERVICE: "WMS",
      REQUEST: "GetMap",
      VERSION: "1.3.0",
      LAYERS: "ORTHOIMAGERY.ORTHOPHOTOS",
      STYLES: "normal",
      CRS: "EPSG:4326",
      BBOX: bbox,
      WIDTH: String(VIEW_W * 2),
      HEIGHT: String(VIEW_H * 2),
      FORMAT: "image/jpeg",
    });
    const imageUrl = `${WMS_URL}?${qs.toString()}`;

    return { project, imageUrl };
  }, [subjectGeom]);

  if (!view || !subjectGeom) {
    return (
      <div
        className={`relative rounded-xl2 overflow-hidden bg-ink-50 flex items-center justify-center ${className}`}
      >
        <div className="text-[11.5px] text-ink-500">
          Pas d'imagerie satellite pour cette parcelle
        </div>
      </div>
    );
  }

  const subjectPath = geomToSvgPath(subjectGeom, view.project);

  return (
    <div
      className={`relative rounded-xl2 overflow-hidden hairline bg-ink-900 ${className}`}
    >
      <img
        src={view.imageUrl}
        alt={`Vue satellite — parcelle ${parcelLabel(parcel)}`}
        className="absolute inset-0 w-full h-full object-cover"
        loading="lazy"
        // If WMS hiccups (rare), fall back to a neutral gray rather than a
        // broken-image icon — the SVG overlay still renders on top.
        onError={(e) => {
          (e.currentTarget as HTMLImageElement).style.opacity = "0";
        }}
      />
      <svg
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        preserveAspectRatio="xMidYMid meet"
        className="absolute inset-0 w-full h-full"
      >
        {/* Parcel outline over the orthophoto. Stronger stroke than the
            cadastre view since the satellite background is busy. */}
        <path
          d={subjectPath}
          fill="rgba(217,234,44,0.20)"
          stroke="#D9EA2C"
          strokeWidth="2.2"
          strokeLinejoin="round"
          style={{ filter: "drop-shadow(0 0 2px rgba(0,0,0,0.55))" }}
        />
      </svg>

      {/* IGN attribution — required when republishing orthophotos. */}
      <div className="absolute bottom-2 right-2 text-[9px] font-mono uppercase tracking-widest text-white/85 bg-black/45 px-1.5 py-0.5 rounded">
        © IGN — Géoplateforme
      </div>

      <div className="absolute top-3 left-3">
        <Pill tone="soft" icon={<Ic.Pin size={12} />}>
          Parcelle {parcelLabel(parcel)}
        </Pill>
      </div>
    </div>
  );
}

// ─── Geometry helpers ────────────────────────────────────────────────────
// Intentionally duplicated from ParcelMap.tsx to keep each map component
// self-contained. If a 3rd map variant lands we extract to src/lib/geom.ts.

type Bbox = { minLng: number; minLat: number; maxLng: number; maxLat: number };

function bboxOfGeom(geom: AnyGeom): Bbox | null {
  if (!geom) return null;
  let minLng = Infinity,
    minLat = Infinity,
    maxLng = -Infinity,
    maxLat = -Infinity;
  const visit = (ring: number[][]) => {
    for (const [lng, lat] of ring) {
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
    }
  };
  if (geom.type === "Polygon") geom.coordinates.forEach(visit);
  else if (geom.type === "MultiPolygon")
    geom.coordinates.forEach((p) => p.forEach(visit));
  if (!Number.isFinite(minLng)) return null;
  return { minLng, minLat, maxLng, maxLat };
}

function expandBbox(bb: Bbox, meters: number): Bbox {
  const dLat = meters / 111_320;
  const centerLat = (bb.minLat + bb.maxLat) / 2;
  const dLng = meters / (111_320 * Math.cos((centerLat * Math.PI) / 180));
  return {
    minLng: bb.minLng - dLng,
    maxLng: bb.maxLng + dLng,
    minLat: bb.minLat - dLat,
    maxLat: bb.maxLat + dLat,
  };
}

// Expand bbox so its *metric* aspect ratio (i.e. post-cos(lat)) matches the
// target. We extend along whichever axis is too short, keeping the parcel
// centered in the padded view.
function fitToMetricRatio(bb: Bbox, targetRatio: number): Bbox {
  const centerLat = (bb.minLat + bb.maxLat) / 2;
  const cosLat = Math.cos((centerLat * Math.PI) / 180);
  const dyDeg = bb.maxLat - bb.minLat;
  const dxMetric = (bb.maxLng - bb.minLng) * cosLat;
  const currentRatio = dxMetric / dyDeg;
  let { minLng, maxLng, minLat, maxLat } = bb;
  if (currentRatio < targetRatio) {
    // Too tall — widen longitude span.
    const targetDxMetric = dyDeg * targetRatio;
    const addMetric = targetDxMetric - dxMetric;
    const addLng = addMetric / 2 / cosLat;
    minLng -= addLng;
    maxLng += addLng;
  } else if (currentRatio > targetRatio) {
    // Too wide — grow latitude span.
    const targetDy = dxMetric / targetRatio;
    const addLat = (targetDy - dyDeg) / 2;
    minLat -= addLat;
    maxLat += addLat;
  }
  return { minLng, maxLng, minLat, maxLat };
}

function geomToSvgPath(
  geom: AnyGeom,
  project: (lng: number, lat: number) => number[],
): string {
  if (!geom) return "";
  const ringPath = (ring: number[][]) => {
    if (!ring.length) return "";
    const [x0, y0] = project(ring[0][0], ring[0][1]);
    let d = `M${x0.toFixed(2)} ${y0.toFixed(2)}`;
    for (let i = 1; i < ring.length; i++) {
      const [x, y] = project(ring[i][0], ring[i][1]);
      d += ` L${x.toFixed(2)} ${y.toFixed(2)}`;
    }
    return d + " Z";
  };
  if (geom.type === "Polygon") return geom.coordinates.map(ringPath).join(" ");
  if (geom.type === "MultiPolygon")
    return geom.coordinates
      .map((rings) => rings.map(ringPath).join(" "))
      .join(" ");
  return "";
}
